import {
  Autocomplete,
  Card,
  Divider,
  Group,
  ScrollArea,
  Space,
  Stack,
  Switch,
  Table,
  Tabs,
} from "@mantine/core";
import { Text } from "@/components/ui/Text";
import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useStyles } from "@/styles/useStyles";
import { IconArrowRight, IconCheck, IconUpload } from "@tabler/icons-react";
import { TextInput } from "@/components/ui/TextInput";
import { NumberInput } from "@/components/ui/NumberInput";
import { useLocalStorage } from "@mantine/hooks";
import { Button } from "@/components/ui/Button";
import { Link } from "@/components/ui/Link";
import { AdminService } from "@/services/AdminService";
import { Notifications } from "@/services/Notifications";
import { useIsAdmin, usePartners } from "@/store/selectors";

type AdminUploadKey =
  | "assignmentResponses"
  | "assignments"
  | "videoDataByUser"
  | "users"
  | "userAssignments";

type AdminUploadStatus = Record<AdminUploadKey, boolean>;

const EMPTY_UPLOAD_STATUS: AdminUploadStatus = {
  assignmentResponses: false,
  assignments: false,
  videoDataByUser: false,
  users: false,
  userAssignments: false,
};

const REQUIRED_ADMIN_UPLOADS: Array<{
  key: AdminUploadKey;
  title: string;
  description: string;
  expectedFileName: string;
  dataUrl: string;
  optional?: boolean;
}> = [
  {
    key: "assignmentResponses",
    title: "Assignment Responses",
    description: "Upload assignment responses export JSON.",
    expectedFileName:
      "advize-production-default-rtdb-assignmentResponses-export.json",
    dataUrl:
      "https://console.firebase.google.com/project/advize-production/database/advize-production-default-rtdb/data/~2FassignmentResponses",
  },
  {
    key: "assignments",
    title: "Assignments",
    description: "Upload assignments export JSON.",
    expectedFileName: "advize-production-default-rtdb-assignments-export.json",
    dataUrl:
      "https://console.firebase.google.com/project/advize-production/database/advize-production-default-rtdb/data/~2Fassignments",
  },
  {
    key: "videoDataByUser",
    title: "Video Data By User",
    description: "Upload video data by user export JSON.",
    expectedFileName:
      "advize-production-default-rtdb-videoDataByUser-export.json",
    dataUrl:
      "https://console.firebase.google.com/project/advize-production/database/advize-production-default-rtdb/data/~2FvideoDataByUser",
  },
  {
    key: "users",
    title: "Users",
    description: "Upload users export JSON.",
    expectedFileName: "advize-production-default-rtdb-users-export.json",
    dataUrl:
      "https://console.firebase.google.com/project/advize-production/database/advize-production-default-rtdb/data/~2Fusers",
  },
  {
    key: "userAssignments",
    title: "User Assignments",
    description:
      "Upload user assignments export JSON. Used by the Seats tab to detect when a student joined/started an assignment (enables the video-watch-after-join qualified-seat signal).",
    expectedFileName:
      "advize-production-default-rtdb-userAssignments-export.json",
    dataUrl:
      "https://console.firebase.google.com/project/advize-production/database/advize-production-default-rtdb/data/~2FuserAssignments",
  },
];

export function AdminAssignments() {
  const { theme } = useStyles();
  const isLocalhost = window.location.hostname === "localhost";
  const isAdmin = useIsAdmin();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [USERS, setUSERS] = useState<any>({});
  const [ASSIGNMENTS, setASSIGNMENTS] = useState<any>({});
  const [VIDEO_DATA_BY_USER, setVIDEO_DATA_BY_USER] = useState<any>({});
  const [ASSIGNMENT_RESPONSES, setASSIGNMENT_RESPONSES] = useState<any>({});
  const [USER_ASSIGNMENTS, setUSER_ASSIGNMENTS] = useState<any>({});
  const [uploadedAdminFiles, setUploadedAdminFiles] =
    useState<AdminUploadStatus>(EMPTY_UPLOAD_STATUS);
  const [selectedAssignmentId, setSelectedAssignmentId] = useLocalStorage({
    key: "selectedAssignmentId",
    defaultValue: null,
  });
  const [showBlacklistedUsers, setShowBlacklistedUsers] = useState(false);
  const [hideUsersWithoutAssignments, setHideUsersWithoutAssignments] =
    useState(false);
  const [hideUsersWithoutStudents, setHideUsersWithoutStudents] =
    useState(false);
  const [search, setSearch] = useState("");
  const [adminMainTab, setAdminMainTab] = useState(
    isLocalhost ? "assignments" : "uploads",
  );

  const loadData = useCallback(async () => {
    if (isLocalhost) {
      try {
        // Fetch from public directory
        const [responsesRes, assignmentsRes, videoDataRes, usersRes] =
          await Promise.all([
            fetch(
              "/doNotCommit/advize-production-default-rtdb-assignmentResponses-export.json",
            ),
            fetch(
              "/doNotCommit/advize-production-default-rtdb-assignments-export.json",
            ),
            fetch(
              "/doNotCommit/advize-production-default-rtdb-videoDataByUser-export.json",
            ),
            fetch(
              "/doNotCommit/advize-production-default-rtdb-users-export.json",
            ),
          ]);

        const users = await usersRes.json();
        const assignments = await assignmentsRes.json();
        const responses = await responsesRes.json();
        const videoData = await videoDataRes.json();

        // Parse JSON responses
        setASSIGNMENT_RESPONSES(responses);
        setASSIGNMENTS(assignments);
        setVIDEO_DATA_BY_USER(videoData);
        setUSERS(users);
        setUploadedAdminFiles((prev) => ({
          ...prev,
          assignmentResponses: true,
          assignments: true,
          videoDataByUser: true,
          users: true,
        }));
      } catch (error) {
        console.warn("Could not load admin data files:", error);
      }

      // Optional: default to the local userAssignments export if it exists.
      // Missing/invalid file is tolerated so it never blocks the required four.
      try {
        const userAssignmentsRes = await fetch(
          "/doNotCommit/advize-production-default-rtdb-userAssignments-export.json",
        );
        if (userAssignmentsRes.ok) {
          const userAssignments = await userAssignmentsRes.json();
          if (
            userAssignments &&
            typeof userAssignments === "object" &&
            !Array.isArray(userAssignments)
          ) {
            setUSER_ASSIGNMENTS(userAssignments);
            setUploadedAdminFiles((prev) => ({
              ...prev,
              userAssignments: true,
            }));
          }
        }
      } catch (error) {
        console.warn("Could not load optional userAssignments file:", error);
      }
    }
  }, [isLocalhost]);

  useEffect(() => {
    loadData().then(() => setDataLoaded(true));
  }, [loadData]);

  const partnerUsers = useMemo(
    () =>
      Object.values(USERS)
        .filter((user: any) => {
          return !!user.partnerData;
        })
        .filter((user: any) => {
          if (showBlacklistedUsers) return true;
          const blacklist = [
            "ryanjyost@gmail.com",
            "demo.edu",
            "emilytest",
            "yosttest",
            "educatordemo",
            "test_educator",
            "emilymcsherry",
            "ryost217",
            "emily.mcsherry",
            "advizetest",
            "@test.com",
            "educator_demo",
            "demo_educator",
            "yostinternal",
          ];
          return !blacklist.some((txt) => user.email.includes(txt));
        })
        .filter((user: any) => {
          if (!search) return true;
          return user.email.toLowerCase().includes(search.toLowerCase());
        })
        .sort((a: any, b: any) => b.createdAt - a.createdAt),
    [USERS, search, showBlacklistedUsers],
  );

  const assignmentIdsByUserId: any = Object.values(ASSIGNMENTS as any).reduce(
    (acc: any, assignment: any) => {
      const userWithPartnerRole = Object.entries(assignment.users).find(
        ([, data]: any) => data.role === "owner",
      );

      if (userWithPartnerRole) {
        acc[userWithPartnerRole[0]] = [
          ...(acc[userWithPartnerRole[0]] || []),
          assignment.id,
        ];
      }

      return acc;
    },
    {} as any,
  );

  const displayedPartnerUsers = useMemo(() => {
    return partnerUsers.filter((user: any) => {
      const userAssignmentIds = assignmentIdsByUserId[user.id] || [];

      if (hideUsersWithoutAssignments && userAssignmentIds.length === 0) {
        return false;
      }

      if (hideUsersWithoutStudents) {
        const hasAnyStudents = userAssignmentIds.some(
          (assignmentId: string) => {
            const assignment = (ASSIGNMENTS as any)?.[assignmentId];
            const studentCount = Object.values(assignment?.users || {}).filter(
              (assignmentUser: any) => assignmentUser?.role === "student",
            ).length;
            return studentCount > 0;
          },
        );

        if (!hasAnyStudents) {
          return false;
        }
      }

      return true;
    });
  }, [
    ASSIGNMENTS,
    assignmentIdsByUserId,
    hideUsersWithoutAssignments,
    hideUsersWithoutStudents,
    partnerUsers,
  ]);

  const assignments = useMemo(() => {
    return Object.fromEntries(
      Object.entries(ASSIGNMENTS as any).map(
        ([id, assignment]: [string, any]) => {
          return [
            id,
            {
              ...assignment,
              totalStudents: Object.values(assignment.users).filter(
                (user: any) => user.role === "student",
              ).length,
            } as any,
          ];
        },
      ),
    );
  }, [ASSIGNMENTS]);

  const videoDataByUser = useMemo(() => {
    return VIDEO_DATA_BY_USER;
  }, [VIDEO_DATA_BY_USER]);

  const assignmentResponses = useMemo(() => {
    return ASSIGNMENT_RESPONSES;
  }, [ASSIGNMENT_RESPONSES]);

  const hasAllRequiredAdminFiles = useMemo(() => {
    return REQUIRED_ADMIN_UPLOADS.filter((upload) => !upload.optional).every(
      (upload) => uploadedAdminFiles[upload.key],
    );
  }, [uploadedAdminFiles]);

  const applyUploadedAdminData = useCallback(
    (uploadKey: AdminUploadKey, parsedJson: Record<string, any>) => {
      if (uploadKey === "assignmentResponses") {
        setASSIGNMENT_RESPONSES(parsedJson);
      } else if (uploadKey === "assignments") {
        setASSIGNMENTS(parsedJson);
      } else if (uploadKey === "videoDataByUser") {
        setVIDEO_DATA_BY_USER(parsedJson);
      } else if (uploadKey === "users") {
        setUSERS(parsedJson);
      } else if (uploadKey === "userAssignments") {
        setUSER_ASSIGNMENTS(parsedJson);
      }

      setUploadedAdminFiles((prev) => ({
        ...prev,
        [uploadKey]: true,
      }));
    },
    [],
  );

  if (!isAdmin) {
    return (
      <Stack p="xl" gap="sm">
        <Text titleStyle="h5">Admin access required.</Text>
      </Stack>
    );
  }

  if (isLocalhost && !dataLoaded) {
    return (
      <Stack p="xl" gap="xl">
        <Text>Loading data...</Text>
      </Stack>
    );
  }

  return (
    <Stack mih="100vh" bg="slate.1" gap={0}>
      <Tabs
        value={adminMainTab}
        onChange={(value) => setAdminMainTab(value || "assignments")}
        px="xl"
        pt="md"
      >
        <Tabs.List>
          <Tabs.Tab value="uploads">Data Uploads</Tabs.Tab>
          <Tabs.Tab value="assignments">Assignment Results</Tabs.Tab>
          <Tabs.Tab value="seats">Seats</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="uploads" pt="md" pb="xl">
          <ScrollArea h="calc(100vh - 8rem)" type="auto">
            <AdminDataUploadTab
              uploadedAdminFiles={uploadedAdminFiles}
              onUpload={applyUploadedAdminData}
            />
          </ScrollArea>
        </Tabs.Panel>
        <Tabs.Panel value="assignments" pb="xl">
          {!hasAllRequiredAdminFiles ? (
            <AdminDataRequiredNotice
              onGoToUploads={() => setAdminMainTab("uploads")}
            />
          ) : (
            <Group
              align="flex-start"
              justify="flex-start"
              wrap="nowrap"
              mih="calc(100vh - 3rem)"
            >
              <Stack
                py="xl"
                gap="md"
                miw={620}
                w={620}
                mah="100vh"
                style={{ overflowY: "auto" }}
              >
                <Stack gap="md" w="100%">
                  <TextInput
                    id="search"
                    style={{ borderRadius: theme.radius.xl }}
                    title="Search users"
                    placeholder="Search users"
                    value={search}
                    // onChange={(event) => setSearch(event.currentTarget.value)}
                    onDebouncedChange={(value) => setSearch(value)}
                    onClear={() => setSearch("")}
                  />
                  <Stack gap={6}>
                    <Text size="xs" c="slate.6" fw={600}>
                      Filters
                    </Text>

                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Switch
                        size="xs"
                        label="Hide users without assignments"
                        checked={hideUsersWithoutAssignments}
                        onChange={(event) =>
                          setHideUsersWithoutAssignments(
                            event.currentTarget.checked,
                          )
                        }
                      />
                    </Group>
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Switch
                        size="xs"
                        label="Hide users without students"
                        checked={hideUsersWithoutStudents}
                        onChange={(event) =>
                          setHideUsersWithoutStudents(
                            event.currentTarget.checked,
                          )
                        }
                      />
                    </Group>
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Switch
                        size="xs"
                        label="Show internal users"
                        checked={showBlacklistedUsers}
                        onChange={(event) =>
                          setShowBlacklistedUsers(event.currentTarget.checked)
                        }
                      />
                    </Group>
                    <Space h="xs" />
                    <Divider c="slate.3" />
                    <Space h={4} />
                    <Text size="md" fw={600} c="slate.6" w="100%" ta="center">
                      {displayedPartnerUsers.length} matching users
                    </Text>
                  </Stack>
                </Stack>
                {displayedPartnerUsers.map((user: any) => {
                  const userAssignmentIds = assignmentIdsByUserId[user.id];
                  const userAssignments = (userAssignmentIds || [])
                    .map(
                      (assignmentId: any) => (assignments as any)[assignmentId],
                    )
                    .filter(Boolean)
                    .sort(
                      (a: any, b: any) =>
                        (b.createdAt || 0) - (a.createdAt || 0),
                    );

                  return (
                    <Stack
                      key={user.id}
                      gap={0}
                      p="md"
                      bg="slate.0"
                      style={{
                        boxShadow: theme.shadows.md,
                        borderRadius: theme.radius.md,
                        border: `1px solid ${theme.colors.border[2]}`,
                      }}
                    >
                      <Text titleStyle="h5">{user.email}</Text>
                      <Text size="xs" c="slate.6">
                        Created at: {new Date(user.createdAt).toLocaleString()}
                      </Text>
                      <Space h="xs" />
                      <Divider c="slate.3" />
                      <Space h="xs" />
                      {!userAssignmentIds?.length ? (
                        <Text size="xs" c="red">
                          No assignments
                        </Text>
                      ) : (
                        <Table.ScrollContainer minWidth={560}>
                          <Table
                            striped
                            highlightOnHover
                            withTableBorder
                            withColumnBorders
                          >
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Title</Table.Th>
                                <Table.Th style={{ width: 80 }}>
                                  Students
                                </Table.Th>
                                <Table.Th style={{ width: 210 }}>
                                  Created At
                                </Table.Th>
                                <Table.Th style={{ width: 48 }} />
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {userAssignments.map((assignment: any) => {
                                const assignmentId = assignment.id;
                                const totalStudents =
                                  assignment.totalStudents || 0;
                                const isRemoved = !!assignment.removed;
                                const isActive =
                                  selectedAssignmentId === assignmentId;
                                const assignmentTitle =
                                  assignment.title || "Untitled";

                                return (
                                  <Table.Tr
                                    key={assignmentId}
                                    onClick={() =>
                                      setSelectedAssignmentId(assignmentId)
                                    }
                                    style={{
                                      cursor: "pointer",
                                      backgroundColor: isActive
                                        ? theme.colors.green[0]
                                        : undefined,
                                    }}
                                  >
                                    <Table.Td>
                                      <Text
                                        size="xs"
                                        c={
                                          isActive
                                            ? "green.8"
                                            : totalStudents > 0
                                              ? "primary.8"
                                              : "slate.6"
                                        }
                                        style={{
                                          textDecoration: isRemoved
                                            ? "line-through"
                                            : "none",
                                        }}
                                      >
                                        {assignmentTitle}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text size="xs">{totalStudents}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text size="xs" c="slate.6">
                                        {assignment.createdAt
                                          ? new Date(
                                              assignment.createdAt,
                                            ).toLocaleString()
                                          : "Unknown"}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td>
                                      <Group justify="flex-end">
                                        {isActive ? (
                                          <IconCheck
                                            size={16}
                                            color="#2f9e44"
                                          />
                                        ) : (
                                          <IconArrowRight
                                            size={16}
                                            color={theme.colors.slate[5]}
                                          />
                                        )}
                                      </Group>
                                    </Table.Td>
                                  </Table.Tr>
                                );
                              })}
                            </Table.Tbody>
                          </Table>
                        </Table.ScrollContainer>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
              <AssignmentInfo
                users={USERS}
                videoDataByUser={videoDataByUser}
                assignmentResponses={assignmentResponses}
                assignmentData={
                  selectedAssignmentId
                    ? (assignments as any)[selectedAssignmentId]
                    : null
                }
              />
            </Group>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="seats" pt="md" pb="xl">
          {!hasAllRequiredAdminFiles ? (
            <AdminDataRequiredNotice
              onGoToUploads={() => setAdminMainTab("uploads")}
            />
          ) : (
            <ScrollArea h="calc(100vh - 8rem)" type="auto">
              <AdminSeatsTab
                assignments={ASSIGNMENTS}
                users={USERS}
                assignmentResponses={ASSIGNMENT_RESPONSES}
                videoDataByUser={VIDEO_DATA_BY_USER}
                userAssignments={USER_ASSIGNMENTS}
              />
            </ScrollArea>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function AdminDataRequiredNotice(props: { onGoToUploads: () => void }) {
  const { onGoToUploads } = props;
  return (
    <Stack p="xl" gap="sm">
      <Text titleStyle="h5">Admin data files are required first.</Text>
      <Text size="sm" c="slate.6">
        Go to Data Uploads and upload all four required JSON files using their
        exact filenames.
      </Text>
      <Group>
        <Button
          variant="filled"
          color="primary"
          leftSection={<IconUpload size={16} />}
          onClick={onGoToUploads}
        >
          Go to Data Uploads
        </Button>
      </Group>
    </Stack>
  );
}

function AdminDataUploadTab(props: {
  uploadedAdminFiles: AdminUploadStatus;
  onUpload: (
    uploadKey: AdminUploadKey,
    parsedJson: Record<string, any>,
  ) => void;
}) {
  const { uploadedAdminFiles, onUpload } = props;
  const [uploadStatusByKey, setUploadStatusByKey] = useState<
    Record<AdminUploadKey, string>
  >({
    assignmentResponses: "",
    assignments: "",
    videoDataByUser: "",
    users: "",
    userAssignments: "",
  });

  const handleFileChange = useCallback(
    async (
      uploadKey: AdminUploadKey,
      expectedFileName: string,
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      const selectedFile = event.currentTarget.files?.[0];
      if (!selectedFile) return;

      if (selectedFile.name !== expectedFileName) {
        setUploadStatusByKey((prev) => ({
          ...prev,
          [uploadKey]:
            "Invalid filename. Upload the exact file shown in this slot.",
        }));
        return;
      }

      try {
        const fileText = await selectedFile.text();
        const parsedJson = JSON.parse(fileText);

        if (
          parsedJson === null ||
          typeof parsedJson !== "object" ||
          Array.isArray(parsedJson)
        ) {
          throw new Error("File content must be a JSON object.");
        }

        onUpload(uploadKey, parsedJson);
        setUploadStatusByKey((prev) => ({
          ...prev,
          [uploadKey]: `Uploaded: ${selectedFile.name}`,
        }));
      } catch (error) {
        setUploadStatusByKey((prev) => ({
          ...prev,
          [uploadKey]:
            error instanceof Error
              ? error.message
              : "Could not parse JSON file.",
        }));
      } finally {
        if (event.currentTarget) {
          event.currentTarget.value = "";
        }
      }
    },
    [onUpload],
  );

  const requiredUploadCount = useMemo(
    () => REQUIRED_ADMIN_UPLOADS.filter((upload) => !upload.optional).length,
    [],
  );

  const uploadedCount = useMemo(() => {
    return REQUIRED_ADMIN_UPLOADS.filter(
      (upload) => !upload.optional && uploadedAdminFiles[upload.key],
    ).length;
  }, [uploadedAdminFiles]);

  return (
    <Stack p="xl" gap="md" maw={950}>
      <Text titleStyle="h4">Upload Admin Data Files</Text>
      <Text size="sm" c="slate.6">
        This upload flow only keeps data in memory for the current page session.
        Nothing is persisted to local storage.
      </Text>
      <Text
        size="sm"
        c={uploadedCount === requiredUploadCount ? "green.7" : "yellow.8"}
      >
        Uploaded required slots: {uploadedCount} / {requiredUploadCount}
      </Text>

      {REQUIRED_ADMIN_UPLOADS.map((uploadDef) => {
        const isUploaded = uploadedAdminFiles[uploadDef.key];
        const statusMessage = uploadStatusByKey[uploadDef.key];
        return (
          <Card
            key={uploadDef.key}
            p="md"
            bg={isUploaded ? "green.0" : "white"}
            style={{
              border: isUploaded ? "1px solid #8ce99a" : "1px solid #dee2e6",
            }}
          >
            <Stack gap="xs">
              <Text titleStyle="h5">{uploadDef.title}</Text>
              <Text size="sm" c="slate.6">
                {uploadDef.description}
              </Text>
              <Text size="sm">
                <strong>Exact filename required:</strong>{" "}
                {uploadDef.expectedFileName}
              </Text>
              <Text size="sm">
                <strong>Data URL:</strong>{" "}
                <a
                  href={uploadDef.dataUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {uploadDef.dataUrl}
                </a>
              </Text>
              <Group align="center">
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(event) =>
                    handleFileChange(
                      uploadDef.key,
                      uploadDef.expectedFileName,
                      event,
                    )
                  }
                />
                <Text size="sm" c={isUploaded ? "green.7" : "slate.6"}>
                  {isUploaded ? "Ready" : "Not uploaded"}
                </Text>
              </Group>
              {statusMessage ? (
                <Text
                  size="xs"
                  c={
                    statusMessage.toLowerCase().includes("uploaded")
                      ? "green.7"
                      : "red.7"
                  }
                >
                  {statusMessage}
                </Text>
              ) : null}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

/**
 * Determines whether a student has "started" (In Progress) a specific
 * assignment and, if so, the earliest timestamp of qualifying activity.
 *
 * A seat is "qualified" when the student has engaged after being added:
 *   - has any saved response for the assignment (per-assignment signal), OR
 *   - has watched any video after joining the assignment (join anchor comes
 *     from the userAssignments node: `consent` preferred, else `lastVisitedAt`).
 *
 * `startedAt` is null when the student clearly started but no activity carries a
 * timestamp (legacy responses without `lastUpdatedAt` and no join anchor). These
 * "undated" starts are counted for all-time totals but cannot be placed inside a
 * specific date range.
 */
function getStudentAssignmentStartEvent(params: {
  assignmentId: string;
  studentUserId: string;
  assignmentResponses: Record<string, any>;
  videoDataByUser: Record<string, any>;
  userAssignments: Record<string, any>;
}): { started: boolean; startedAt: number | null } {
  const {
    assignmentId,
    studentUserId,
    assignmentResponses,
    videoDataByUser,
    userAssignments,
  } = params;

  const candidateTimestamps: number[] = [];
  let started = false;

  const responses =
    assignmentResponses?.[assignmentId]?.[studentUserId]?.responses;
  if (responses && typeof responses === "object") {
    for (const entry of Object.values(responses) as any[]) {
      const response = entry?.response;
      if (response === undefined || response === null || response === "") {
        continue;
      }
      started = true;
      const timestamp = Number(entry?.lastUpdatedAt);
      if (Number.isFinite(timestamp)) {
        candidateTimestamps.push(timestamp);
      }
    }
  }

  const joinRecord = userAssignments?.[studentUserId]?.[assignmentId];
  const joinAt = Number(joinRecord?.consent ?? joinRecord?.lastVisitedAt);
  if (Number.isFinite(joinAt)) {
    const videos = videoDataByUser?.[studentUserId]?.videos;
    if (videos && typeof videos === "object") {
      for (const video of Object.values(videos) as any[]) {
        const watchedAt = Number(video?.lastUpdatedAt);
        if (Number.isFinite(watchedAt) && watchedAt >= joinAt) {
          started = true;
          candidateTimestamps.push(watchedAt);
        }
      }
    }
  }

  if (!started) {
    return { started: false, startedAt: null };
  }

  return {
    started: true,
    startedAt: candidateTimestamps.length
      ? Math.min(...candidateTimestamps)
      : null,
  };
}

function AdminSeatsTab(props: {
  assignments: Record<string, any>;
  users: Record<string, any>;
  assignmentResponses: Record<string, any>;
  videoDataByUser: Record<string, any>;
  userAssignments: Record<string, any>;
}) {
  const formatNumber = useCallback(
    (value: number) => value.toLocaleString(),
    [],
  );
  const {
    assignments,
    users,
    assignmentResponses,
    videoDataByUser,
    userAssignments,
  } = props;
  const [seatsStartDate, setSeatsStartDate] = useState("");
  const [seatsEndDate, setSeatsEndDate] = useState("");
  const [seatsSubTab, setSeatsSubTab] = useState("partners");
  const partners = usePartners();
  const [seatsPartnerSlug, setSeatsPartnerSlug] = useState("");
  const [seatsValue, setSeatsValue] = useState<number | "">(0);
  const [isSavingSeats, setIsSavingSeats] = useState(false);
  const [isLoadingPartnerSeats, setIsLoadingPartnerSeats] = useState(false);
  const [partnerSeatsBySlug, setPartnerSeatsBySlug] = useState<
    Record<string, { seats?: number; updatedAt?: number }>
  >({});
  const [seatsEmail, setSeatsEmail] = useState("");
  const [emailSeatsValue, setEmailSeatsValue] = useState<number | "">(0);
  const [isSavingEmailSeats, setIsSavingEmailSeats] = useState(false);
  const [isLoadingEmailSeats, setIsLoadingEmailSeats] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [emailSeatsByEmail, setEmailSeatsByEmail] = useState<
    Record<string, { seats?: number; updatedAt?: number }>
  >({});

  const loadPartnerSeats = useCallback(async () => {
    try {
      setIsLoadingPartnerSeats(true);
      const seatsData = await AdminService.getPartnerSeats();
      setPartnerSeatsBySlug(seatsData);
    } catch (error) {
      Notifications.showNotification({
        title: "Could not load partner seats",
        message:
          error instanceof Error ? error.message : "An unknown error occurred.",
        type: "error",
      });
    } finally {
      setIsLoadingPartnerSeats(false);
    }
  }, []);

  useEffect(() => {
    loadPartnerSeats();
  }, [loadPartnerSeats]);

  const loadEmailSeats = useCallback(async () => {
    try {
      setIsLoadingEmailSeats(true);
      const seatsData = await AdminService.getEmailSeats();
      setEmailSeatsByEmail(seatsData);
    } catch (error) {
      Notifications.showNotification({
        title: "Could not load email seats",
        message:
          error instanceof Error ? error.message : "An unknown error occurred.",
        type: "error",
      });
    } finally {
      setIsLoadingEmailSeats(false);
    }
  }, []);

  useEffect(() => {
    loadEmailSeats();
  }, [loadEmailSeats]);

  const updatePartnerSeats = useCallback(async () => {
    if (!seatsPartnerSlug.trim()) {
      Notifications.showNotification({
        title: "Missing partner slug",
        message: "Enter a partner slug to update seats.",
        type: "warning",
      });
      return;
    }

    if (seatsValue === "" || Number(seatsValue) < 0) {
      Notifications.showNotification({
        title: "Invalid seats value",
        message: "Seats must be a non-negative number.",
        type: "warning",
      });
      return;
    }

    try {
      setIsSavingSeats(true);
      await AdminService.updatePartnerSeats({
        partnerSlug: seatsPartnerSlug,
        seats: Number(seatsValue),
      });

      Notifications.showNotification({
        title: "Seats updated",
        message: `Updated seats for ${seatsPartnerSlug.trim()}.`,
        type: "success",
      });
      await loadPartnerSeats();
      setSeatsPartnerSlug("");
    } catch (error) {
      Notifications.showNotification({
        title: "Failed to update seats",
        message:
          error instanceof Error ? error.message : "An unknown error occurred.",
        type: "error",
      });
    } finally {
      setIsSavingSeats(false);
    }
  }, [loadPartnerSeats, seatsPartnerSlug, seatsValue]);

  const deletePartnerSeats = useCallback(
    async (partnerSlug: string) => {
      try {
        await AdminService.deletePartnerSeats({ partnerSlug });
        await loadPartnerSeats();

        if (seatsPartnerSlug === partnerSlug) {
          setSeatsPartnerSlug("");
        }
      } catch (error) {
        Notifications.showNotification({
          title: "Failed to delete seats",
          message:
            error instanceof Error
              ? error.message
              : "An unknown error occurred.",
          type: "error",
        });
      }
    },
    [loadPartnerSeats, seatsPartnerSlug],
  );

  const updateEmailSeats = useCallback(async () => {
    if (!seatsEmail.trim()) {
      Notifications.showNotification({
        title: "Missing email",
        message: "Enter an email to update seats.",
        type: "warning",
      });
      return;
    }

    if (emailSeatsValue === "" || Number(emailSeatsValue) < 0) {
      Notifications.showNotification({
        title: "Invalid seats value",
        message: "Seats must be a non-negative number.",
        type: "warning",
      });
      return;
    }

    try {
      setIsSavingEmailSeats(true);
      await AdminService.updateEmailSeats({
        email: seatsEmail,
        seats: Number(emailSeatsValue),
        users,
      });

      Notifications.showNotification({
        title: "Email seats updated",
        message: `Updated seats for ${seatsEmail.trim().toLowerCase()}.`,
        type: "success",
      });
      await loadEmailSeats();
      setSeatsEmail("");
    } catch (error) {
      Notifications.showNotification({
        title: "Failed to update email seats",
        message:
          error instanceof Error ? error.message : "An unknown error occurred.",
        type: "error",
      });
    } finally {
      setIsSavingEmailSeats(false);
    }
  }, [emailSeatsValue, loadEmailSeats, seatsEmail, users]);

  const deleteEmailSeats = useCallback(
    async (email: string) => {
      try {
        await AdminService.deleteEmailSeats({ email });
        await loadEmailSeats();

        if (seatsEmail === email) {
          setSeatsEmail("");
        }
      } catch (error) {
        Notifications.showNotification({
          title: "Failed to delete email seats",
          message:
            error instanceof Error
              ? error.message
              : "An unknown error occurred.",
          type: "error",
        });
      }
    },
    [loadEmailSeats, seatsEmail],
  );

  const partnerSeatsList = useMemo(() => {
    const allPartnerSlugs = Array.from(
      new Set([
        ...(partners || []).map((partner: any) => partner.slug).filter(Boolean),
        ...Object.keys(partnerSeatsBySlug),
      ]),
    ).sort((a, b) => a.localeCompare(b));

    return allPartnerSlugs.map((partnerSlug) => {
      const data = partnerSeatsBySlug[partnerSlug];
      return {
        partnerSlug,
        seats: Number(data?.seats || 0),
        updatedAt: data?.updatedAt,
        isExplicitAllocation: !!data,
      };
    });
  }, [partnerSeatsBySlug, partners]);

  const partnerSlugOptions = useMemo(() => {
    const slugs = (partners || [])
      .map((partner: any) => partner.slug)
      .filter(Boolean);
    return Array.from(new Set(slugs)).sort((a, b) => a.localeCompare(b));
  }, [partners]);

  const partnerBySlug = useMemo(() => {
    return (partners || []).reduce((acc: Record<string, any>, partner: any) => {
      if (partner?.slug) {
        acc[partner.slug] = partner;
      }
      return acc;
    }, {});
  }, [partners]);

  const ownerEmailsWithoutPartner = useMemo(() => {
    const ownerEmails = Object.values(assignments || {})
      .map((assignment: any) => {
        const ownerEntry = Object.entries(assignment?.users || {}).find(
          ([, userData]: any) => userData?.role === "owner",
        );
        const ownerUserId = ownerEntry?.[0];
        if (!ownerUserId) return null;

        const owner = users?.[ownerUserId];
        const ownerPartnerSlug =
          owner?.partnerData?.partnerSlug || owner?.partnerSlug;
        const ownerPartnerId =
          owner?.partnerData?.partnerId || owner?.partnerId;
        const ownerEmail = owner?.email
          ? String(owner.email).toLowerCase().trim()
          : "";

        // Email limits are for assignment owners who are not mapped to a partner.
        if (!ownerEmail || ownerPartnerSlug || ownerPartnerId) {
          return null;
        }

        return ownerEmail;
      })
      .filter(Boolean) as string[];

    return Array.from(new Set(ownerEmails)).sort((a, b) => a.localeCompare(b));
  }, [assignments, users]);

  const ownerEmailOptions = useMemo(
    () => ownerEmailsWithoutPartner,
    [ownerEmailsWithoutPartner],
  );

  const { rangeStartMs, rangeEndMs, hasDateRange } = useMemo(() => {
    const parsedStart = seatsStartDate
      ? new Date(`${seatsStartDate}T00:00:00`).getTime()
      : NaN;
    const parsedEnd = seatsEndDate
      ? new Date(`${seatsEndDate}T23:59:59.999`).getTime()
      : NaN;
    const startMs = Number.isFinite(parsedStart) ? parsedStart : null;
    const endMs = Number.isFinite(parsedEnd) ? parsedEnd : null;
    return {
      rangeStartMs: startMs,
      rangeEndMs: endMs,
      hasDateRange: startMs != null || endMs != null,
    };
  }, [seatsStartDate, seatsEndDate]);

  // Single pass over assignments building qualified "start" events, deduped per
  // student, grouped by partner slug and by owner email, plus per-assignment.
  const startAggregations = useMemo(() => {
    type SeatStart = { dates: number[]; hasUndated: boolean };
    const partner: Record<string, Record<string, SeatStart>> = {};
    const email: Record<string, Record<string, SeatStart>> = {};
    const enrolledPartner: Record<string, number> = {};
    const enrolledEmail: Record<string, number> = {};
    const perAssignment: Record<
      string,
      Array<{ studentUserId: string; startedAt: number | null }>
    > = {};

    const addStart = (
      map: Record<string, Record<string, SeatStart>>,
      key: string,
      uid: string,
      startedAt: number | null,
    ) => {
      if (!map[key]) map[key] = {};
      if (!map[key][uid]) map[key][uid] = { dates: [], hasUndated: false };
      if (startedAt != null) {
        map[key][uid].dates.push(startedAt);
      } else {
        map[key][uid].hasUndated = true;
      }
    };

    for (const assignment of Object.values(assignments || {}) as any[]) {
      const ownerEntry = Object.entries(assignment?.users || {}).find(
        ([, userData]: any) => userData?.role === "owner",
      );
      const ownerUserId = ownerEntry?.[0];
      if (!ownerUserId) continue;

      const owner = users?.[ownerUserId];
      const partnerSlug = owner?.partnerData?.partnerSlug || owner?.partnerSlug;
      const ownerPartnerId = owner?.partnerData?.partnerId || owner?.partnerId;
      const ownerEmail = owner?.email
        ? String(owner.email).toLowerCase().trim()
        : "";
      const assignmentId = String(assignment?.id || "");

      const students = Object.entries(assignment?.users || {}).filter(
        ([, userData]: any) => userData?.role !== "owner",
      );

      if (partnerSlug) {
        enrolledPartner[partnerSlug] =
          (enrolledPartner[partnerSlug] || 0) + students.length;
      } else if (!ownerPartnerId && ownerEmail) {
        enrolledEmail[ownerEmail] =
          (enrolledEmail[ownerEmail] || 0) + students.length;
      }

      for (const [studentUserId] of students) {
        const { started, startedAt } = getStudentAssignmentStartEvent({
          assignmentId,
          studentUserId,
          assignmentResponses,
          videoDataByUser,
          userAssignments,
        });
        if (!started) continue;

        if (!perAssignment[assignmentId]) perAssignment[assignmentId] = [];
        perAssignment[assignmentId].push({ studentUserId, startedAt });

        if (partnerSlug) {
          addStart(partner, partnerSlug, studentUserId, startedAt);
        } else if (!ownerPartnerId && ownerEmail) {
          addStart(email, ownerEmail, studentUserId, startedAt);
        }
      }
    }

    return { partner, email, enrolledPartner, enrolledEmail, perAssignment };
  }, [
    assignments,
    assignmentResponses,
    users,
    userAssignments,
    videoDataByUser,
  ]);

  const isStartWithinRange = useCallback(
    (startedAt: number | null) => {
      if (startedAt == null) return false;
      if (rangeStartMs != null && startedAt < rangeStartMs) return false;
      if (rangeEndMs != null && startedAt > rangeEndMs) return false;
      return true;
    },
    [rangeEndMs, rangeStartMs],
  );

  const countQualifiedSeats = useCallback(
    (agg: Record<string, { dates: number[]; hasUndated: boolean }>) => {
      let count = 0;
      for (const { dates, hasUndated } of Object.values(agg || {})) {
        if (!hasDateRange) {
          if (dates.length || hasUndated) count += 1;
        } else if (dates.some((date) => isStartWithinRange(date))) {
          count += 1;
        }
      }
      return count;
    },
    [hasDateRange, isStartWithinRange],
  );

  const qualifiedSeatsBySlug = useMemo(() => {
    const result: Record<string, number> = {};
    for (const [slug, agg] of Object.entries(startAggregations.partner)) {
      result[slug] = countQualifiedSeats(agg);
    }
    return result;
  }, [countQualifiedSeats, startAggregations.partner]);

  const qualifiedSeatsByEmail = useMemo(() => {
    const result: Record<string, number> = {};
    for (const [email, agg] of Object.entries(startAggregations.email)) {
      result[email] = countQualifiedSeats(agg);
    }
    return result;
  }, [countQualifiedSeats, startAggregations.email]);

  const startedCountByAssignmentId = useMemo(() => {
    const result: Record<string, number> = {};
    for (const [assignmentId, events] of Object.entries(
      startAggregations.perAssignment,
    )) {
      let count = 0;
      for (const { startedAt } of events) {
        if (!hasDateRange || isStartWithinRange(startedAt)) count += 1;
      }
      result[assignmentId] = count;
    }
    return result;
  }, [hasDateRange, isStartWithinRange, startAggregations.perAssignment]);

  const partnerOwnerEmailTotalsBySlug = useMemo(() => {
    return Object.values(assignments || {}).reduce(
      (acc: Record<string, Record<string, number>>, assignment: any) => {
        const ownerEntry = Object.entries(assignment?.users || {}).find(
          ([, userData]: any) => userData?.role === "owner",
        );
        const ownerUserId = ownerEntry?.[0];
        if (!ownerUserId) return acc;

        const owner = users?.[ownerUserId];
        const partnerSlug =
          owner?.partnerData?.partnerSlug || owner?.partnerSlug;
        const ownerEmail = owner?.email
          ? String(owner.email).toLowerCase().trim()
          : "";
        if (!partnerSlug || !ownerEmail) return acc;

        const totalNonOwnerUsers = Object.values(
          assignment?.users || {},
        ).filter((userData: any) => userData?.role !== "owner").length;

        if (!acc[partnerSlug]) {
          acc[partnerSlug] = {};
        }
        acc[partnerSlug][ownerEmail] =
          (acc[partnerSlug][ownerEmail] || 0) + totalNonOwnerUsers;

        return acc;
      },
      {},
    );
  }, [assignments, users]);

  const partnerAssignmentsBySlug = useMemo(() => {
    return Object.values(assignments || {}).reduce(
      (
        acc: Record<
          string,
          Array<{
            assignmentId: string;
            assignmentTitle: string;
            ownerEmail: string;
            totalStudents: number;
            startedStudents: number;
            createdAt?: number;
          }>
        >,
        assignment: any,
      ) => {
        const ownerEntry = Object.entries(assignment?.users || {}).find(
          ([, userData]: any) => userData?.role === "owner",
        );
        const ownerUserId = ownerEntry?.[0];
        if (!ownerUserId) return acc;

        const owner = users?.[ownerUserId];
        const partnerSlug =
          owner?.partnerData?.partnerSlug || owner?.partnerSlug;
        if (!partnerSlug) return acc;

        const ownerEmail = owner?.email
          ? String(owner.email).toLowerCase().trim()
          : "Unknown";
        const totalStudents = Object.values(assignment?.users || {}).filter(
          (userData: any) => userData?.role === "student",
        ).length;

        if (!acc[partnerSlug]) {
          acc[partnerSlug] = [];
        }

        acc[partnerSlug].push({
          assignmentId: String(assignment?.id || ""),
          assignmentTitle: assignment?.title || "Untitled",
          ownerEmail,
          totalStudents,
          startedStudents:
            startedCountByAssignmentId[String(assignment?.id || "")] || 0,
          createdAt: assignment?.createdAt,
        });

        return acc;
      },
      {},
    );
  }, [assignments, startedCountByAssignmentId, users]);

  const partnerCapacityList = useMemo(() => {
    return partnerSeatsList
      .map((partnerSeat) => {
        const usedSeats = qualifiedSeatsBySlug[partnerSeat.partnerSlug] || 0;
        const allocatedSeats = partnerSeat.seats;
        const overBy = Math.max(usedSeats - allocatedSeats, 0);
        const seatsRemaining = Math.max(allocatedSeats - usedSeats, 0);
        const seatDifference = Math.abs(usedSeats - allocatedSeats);
        const utilization =
          allocatedSeats > 0
            ? usedSeats / allocatedSeats
            : usedSeats > 0
              ? Number.POSITIVE_INFINITY
              : 0;
        const isOverCapacity = usedSeats > allocatedSeats;

        return {
          ...partnerSeat,
          usedSeats,
          allocatedSeats,
          overBy,
          seatsRemaining,
          seatDifference,
          utilization,
          isOverCapacity,
        };
      })
      .sort((a, b) => {
        if (a.isExplicitAllocation !== b.isExplicitAllocation) {
          return a.isExplicitAllocation ? -1 : 1;
        }
        if (a.seatDifference !== b.seatDifference) {
          return b.seatDifference - a.seatDifference;
        }
        if (a.isOverCapacity !== b.isOverCapacity) {
          return a.isOverCapacity ? -1 : 1;
        }
        return a.partnerSlug.localeCompare(b.partnerSlug);
      });
  }, [qualifiedSeatsBySlug, partnerSeatsList]);

  const emailSeatsList = useMemo(() => {
    const allEmails = Array.from(
      new Set([
        ...ownerEmailsWithoutPartner,
        ...Object.keys(emailSeatsByEmail),
      ]),
    ).sort((a, b) => a.localeCompare(b));

    return allEmails.map((email) => {
      const data = emailSeatsByEmail[email];
      return {
        email,
        seats: Number(data?.seats || 0),
        updatedAt: data?.updatedAt,
        isExplicitAllocation: !!data,
      };
    });
  }, [emailSeatsByEmail, ownerEmailsWithoutPartner]);

  const emailCapacityList = useMemo(() => {
    return emailSeatsList
      .map((emailSeat) => {
        const usedSeats = qualifiedSeatsByEmail[emailSeat.email] || 0;
        const allocatedSeats = emailSeat.seats;
        const overBy = Math.max(usedSeats - allocatedSeats, 0);
        const seatsRemaining = Math.max(allocatedSeats - usedSeats, 0);
        const seatDifference = Math.abs(usedSeats - allocatedSeats);
        const utilization =
          allocatedSeats > 0
            ? usedSeats / allocatedSeats
            : usedSeats > 0
              ? Number.POSITIVE_INFINITY
              : 0;
        const isOverCapacity = usedSeats > allocatedSeats;

        return {
          ...emailSeat,
          usedSeats,
          allocatedSeats,
          overBy,
          seatsRemaining,
          seatDifference,
          utilization,
          isOverCapacity,
        };
      })
      .sort((a, b) => {
        if (a.isExplicitAllocation !== b.isExplicitAllocation) {
          return a.isExplicitAllocation ? -1 : 1;
        }
        if (a.seatDifference !== b.seatDifference) {
          return b.seatDifference - a.seatDifference;
        }
        if (a.isOverCapacity !== b.isOverCapacity) {
          return a.isOverCapacity ? -1 : 1;
        }
        return a.email.localeCompare(b.email);
      });
  }, [qualifiedSeatsByEmail, emailSeatsList]);

  const normalizedListSearch = useMemo(
    () => listSearch.trim().toLowerCase(),
    [listSearch],
  );

  const partnerSectionItems = useMemo(() => {
    const items = partnerCapacityList.map((partnerSeat) => ({
      id: partnerSeat.partnerSlug,
      title: partnerBySlug[partnerSeat.partnerSlug]?.title || "Custom Partner",
      subtitle: `${partnerSeat.isExplicitAllocation ? "Explicit partner limit" : "No explicit partner limit configured"} • Slug: ${partnerSeat.partnerSlug}`,
      detailRows: Object.entries(
        partnerOwnerEmailTotalsBySlug[partnerSeat.partnerSlug] || {},
      )
        .sort((a, b) => b[1] - a[1])
        .map(
          ([ownerEmail, totalUsers]) =>
            `${ownerEmail}: ${formatNumber(totalUsers)} users`,
        ),
      assignmentRows: (partnerAssignmentsBySlug[partnerSeat.partnerSlug] || [])
        .slice()
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
      updatedAt: partnerSeat.updatedAt,
      enrolledSeats:
        startAggregations.enrolledPartner[partnerSeat.partnerSlug] || 0,
      usedSeats: partnerSeat.usedSeats,
      allocatedSeats: partnerSeat.allocatedSeats,
      seatsRemaining: partnerSeat.seatsRemaining,
      overBy: partnerSeat.overBy,
      isOverCapacity: partnerSeat.isOverCapacity,
      isExplicitAllocation: partnerSeat.isExplicitAllocation,
      onEdit: () => {
        setSeatsPartnerSlug(partnerSeat.partnerSlug);
        setSeatsValue(partnerSeat.seats);
      },
      onDelete: () => deletePartnerSeats(partnerSeat.partnerSlug),
    }));

    if (!normalizedListSearch) return items;

    return items.filter((item) => {
      const detailText = (item.detailRows || []).join(" ");
      const assignmentText = (item.assignmentRows || [])
        .map(
          (row) =>
            `${row.assignmentTitle} ${row.ownerEmail} ${row.totalStudents}`,
        )
        .join(" ");
      return (
        item.title.toLowerCase().includes(normalizedListSearch) ||
        item.subtitle.toLowerCase().includes(normalizedListSearch) ||
        detailText.toLowerCase().includes(normalizedListSearch) ||
        assignmentText.toLowerCase().includes(normalizedListSearch)
      );
    });
  }, [
    deletePartnerSeats,
    formatNumber,
    normalizedListSearch,
    partnerAssignmentsBySlug,
    partnerBySlug,
    partnerCapacityList,
    partnerOwnerEmailTotalsBySlug,
    startAggregations.enrolledPartner,
  ]);

  const partnerFilteredExplicitCount = useMemo(() => {
    return partnerSectionItems.filter((item) => item.isExplicitAllocation)
      .length;
  }, [partnerSectionItems]);

  const emailSectionItems = useMemo(() => {
    const items = emailCapacityList.map((emailSeat) => ({
      id: emailSeat.email,
      title: emailSeat.email,
      subtitle: emailSeat.isExplicitAllocation
        ? "Explicit email limit"
        : "No explicit email limit configured",
      updatedAt: emailSeat.updatedAt,
      enrolledSeats: startAggregations.enrolledEmail[emailSeat.email] || 0,
      usedSeats: emailSeat.usedSeats,
      allocatedSeats: emailSeat.allocatedSeats,
      seatsRemaining: emailSeat.seatsRemaining,
      overBy: emailSeat.overBy,
      isOverCapacity: emailSeat.isOverCapacity,
      isExplicitAllocation: emailSeat.isExplicitAllocation,
      onEdit: () => {
        setSeatsEmail(emailSeat.email);
        setEmailSeatsValue(emailSeat.seats);
      },
      onDelete: () => deleteEmailSeats(emailSeat.email),
    }));

    if (!normalizedListSearch) return items;

    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(normalizedListSearch) ||
        item.subtitle.toLowerCase().includes(normalizedListSearch),
    );
  }, [
    deleteEmailSeats,
    emailCapacityList,
    normalizedListSearch,
    startAggregations.enrolledEmail,
  ]);

  const emailFilteredExplicitCount = useMemo(() => {
    return emailSectionItems.filter((item) => item.isExplicitAllocation).length;
  }, [emailSectionItems]);

  return (
    <Stack gap="md">
      <Text titleStyle="h4">Partner Seat Capacity</Text>
      <Text size="sm" c="slate.6">
        A seat is counted once a student has <strong>started</strong> ("In
        Progress") an assignment — i.e. watched a video after joining, or saved
        a response. Seats are deduped so each student counts once per partner.
        "Used" below reflects qualified seats; anything over the purchased
        allocation is flagged for invoicing.
      </Text>
      <Card p="sm" bg="slate.0" style={{ border: "1px solid #dee2e6" }}>
        <Stack gap="xs">
          <Text size="xs" fw={600} c="slate.7">
            Qualified seats used within date range (by student start date)
          </Text>
          <Group align="flex-end" wrap="wrap" gap="md">
            <Stack gap={2}>
              <Text size="xs" c="slate.6">
                Start date
              </Text>
              <input
                type="date"
                value={seatsStartDate}
                max={seatsEndDate || undefined}
                onChange={(event) =>
                  setSeatsStartDate(event.currentTarget.value)
                }
              />
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="slate.6">
                End date
              </Text>
              <input
                type="date"
                value={seatsEndDate}
                min={seatsStartDate || undefined}
                onChange={(event) => setSeatsEndDate(event.currentTarget.value)}
              />
            </Stack>
            <Button
              title="Clear date range"
              variant="light"
              color="slate"
              size="xs"
              disabled={!seatsStartDate && !seatsEndDate}
              onClick={() => {
                setSeatsStartDate("");
                setSeatsEndDate("");
              }}
            >
              Clear
            </Button>
            <Text size="xs" c={hasDateRange ? "primary.8" : "slate.6"}>
              {hasDateRange
                ? "Counting seats whose start date falls in the selected range."
                : "No range selected — showing all-time qualified seats."}
            </Text>
          </Group>
          {Object.keys(userAssignments || {}).length === 0 ? (
            <Text size="xs" c="yellow.8">
              User Assignments file not uploaded — the video-watch-after-join
              signal is unavailable, so seats fall back to saved responses only.
              Upload it in the Data Uploads tab for the most accurate counts.
            </Text>
          ) : null}
          {hasDateRange ? (
            <Text size="xs" c="slate.5">
              Note: students who started but whose activity has no timestamp
              (legacy responses without a date and no join anchor) can't be
              placed in a specific range and are excluded from ranged counts.
            </Text>
          ) : null}
        </Stack>
      </Card>
      <TextInput
        id="seatsListSearch"
        title="Search lists"
        placeholder="Search partners, emails, slugs, and owner totals"
        value={listSearch}
        onChange={(event) => setListSearch(event.currentTarget.value)}
        onClear={() => setListSearch("")}
      />
      <Tabs
        value={seatsSubTab}
        onChange={(value) => setSeatsSubTab(value || "partners")}
        w="100%"
      >
        <Tabs.List>
          <Tabs.Tab value="partners">Partner Limits</Tabs.Tab>
          <Tabs.Tab value="emails">Email Limits</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="partners" pt="md">
          <CapacitySection
            sectionTitle="Partner Limits"
            listTitle="Partner Seat Capacity"
            isLoading={isLoadingPartnerSeats}
            onRefresh={loadPartnerSeats}
            emptyMessage={
              normalizedListSearch
                ? "No matching partners found."
                : "No partners found."
            }
            formatNumber={formatNumber}
            secondarySectionStartIndex={partnerFilteredExplicitCount}
            secondarySectionLabel="Partners without explicit seat limits (defaulted to 0 seats)"
            items={partnerSectionItems}
            inputControls={
              <Group align="flex-end" wrap="nowrap" w="100%">
                <Autocomplete
                  id="partnerSlug"
                  label="Partner slug"
                  placeholder="e.g. acme-university"
                  value={seatsPartnerSlug}
                  onChange={setSeatsPartnerSlug}
                  data={partnerSlugOptions}
                  clearable
                  limit={20}
                  style={{ flex: 2 }}
                />
                <NumberInput
                  id="partnerSeats"
                  title="Seats"
                  placeholder="e.g. 100"
                  min={0}
                  value={seatsValue}
                  onChange={(value) =>
                    setSeatsValue(typeof value === "number" ? value : "")
                  }
                  onClear={() => setSeatsValue("")}
                  style={{ flex: 1 }}
                />
                <Button
                  title="Add or update"
                  onClick={updatePartnerSeats}
                  loading={isSavingSeats}
                  miw={140}
                >
                  Add / Update
                </Button>
              </Group>
            }
          />
        </Tabs.Panel>
        <Tabs.Panel value="emails" pt="md">
          <CapacitySection
            sectionTitle="Email Limits"
            listTitle="Email Seat Capacity"
            isLoading={isLoadingEmailSeats}
            onRefresh={loadEmailSeats}
            emptyMessage={
              normalizedListSearch
                ? "No matching emails found."
                : "No assignment owners without partner found."
            }
            formatNumber={formatNumber}
            secondarySectionStartIndex={emailFilteredExplicitCount}
            secondarySectionLabel="Assignment owners without explicit email limits (defaulted to 0 seats)"
            items={emailSectionItems}
            inputControls={
              <Group align="flex-end" wrap="nowrap" w="100%">
                <Autocomplete
                  id="ownerEmail"
                  label="Owner email"
                  placeholder="e.g. owner@school.edu"
                  value={seatsEmail}
                  onChange={setSeatsEmail}
                  data={ownerEmailOptions}
                  clearable
                  limit={20}
                  style={{ flex: 2 }}
                />
                <NumberInput
                  id="emailSeats"
                  title="Seats"
                  placeholder="e.g. 100"
                  min={0}
                  value={emailSeatsValue}
                  onChange={(value) =>
                    setEmailSeatsValue(typeof value === "number" ? value : "")
                  }
                  onClear={() => setEmailSeatsValue("")}
                  style={{ flex: 1 }}
                />
                <Button
                  title="Add or update"
                  onClick={updateEmailSeats}
                  loading={isSavingEmailSeats}
                  miw={140}
                >
                  Add / Update
                </Button>
              </Group>
            }
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

type CapacityItem = {
  id: string;
  title: string;
  subtitle: string;
  detailRows?: string[];
  assignmentRows?: Array<{
    assignmentId: string;
    assignmentTitle: string;
    ownerEmail: string;
    totalStudents: number;
    startedStudents: number;
    createdAt?: number;
  }>;
  isExplicitAllocation?: boolean;
  updatedAt?: number;
  enrolledSeats: number;
  usedSeats: number;
  allocatedSeats: number;
  seatsRemaining: number;
  overBy: number;
  isOverCapacity: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

function CapacitySection(props: {
  sectionTitle: string;
  listTitle: string;
  inputControls: ReactNode;
  isLoading: boolean;
  onRefresh: () => void;
  items: CapacityItem[];
  emptyMessage: string;
  formatNumber: (value: number) => string;
  secondarySectionStartIndex?: number;
  secondarySectionLabel?: string;
}) {
  const {
    sectionTitle,
    listTitle,
    inputControls,
    isLoading,
    onRefresh,
    items,
    emptyMessage,
    formatNumber,
    secondarySectionStartIndex,
    secondarySectionLabel,
  } = props;

  const summary = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc.totalEnrolledSeats += item.enrolledSeats;
        acc.totalUsedSeats += item.usedSeats;
        acc.totalAllocatedSeats += item.allocatedSeats;
        if (item.isOverCapacity) {
          acc.overCapacityCount += 1;
          acc.totalSeatsOver += item.overBy;
        }
        return acc;
      },
      {
        totalEnrolledSeats: 0,
        totalUsedSeats: 0,
        totalAllocatedSeats: 0,
        overCapacityCount: 0,
        totalSeatsOver: 0,
      },
    );
  }, [items]);

  return (
    <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
      <Text titleStyle="h5">{sectionTitle}</Text>
      {inputControls}
      <Divider c="slate.3" />
      <Group justify="space-between" align="center">
        <Text titleStyle="h6">{listTitle}</Text>
        <Button
          title="Refresh list"
          variant="light"
          color="slate"
          size="xs"
          onClick={onRefresh}
          loading={isLoading}
        >
          Refresh
        </Button>
      </Group>
      <Card
        p="sm"
        bg={summary.overCapacityCount > 0 ? "red.0" : "green.0"}
        style={{
          border:
            summary.overCapacityCount > 0
              ? "1px solid #ffa8a8"
              : "1px solid #b2f2bb",
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xl" align="flex-end" wrap="wrap">
            <Stack gap={0}>
              <Text size="xs" c="slate.6" fw={600}>
                Enrolled
              </Text>
              <Text titleStyle="h5" c="slate.8">
                {formatNumber(summary.totalEnrolledSeats)}
              </Text>
            </Stack>
            <Stack gap={0}>
              <Text size="xs" c="slate.6" fw={600}>
                Started
              </Text>
              <Text
                titleStyle="h5"
                c={summary.overCapacityCount > 0 ? "red.7" : "primary.8"}
              >
                {formatNumber(summary.totalUsedSeats)}
              </Text>
            </Stack>
            <Stack gap={0}>
              <Text size="xs" c="slate.6" fw={600}>
                Seats
              </Text>
              <Text titleStyle="h5" c="slate.8">
                {formatNumber(summary.totalAllocatedSeats)}
              </Text>
            </Stack>
            <Text size="xs" c="slate.7">
              {summary.overCapacityCount > 0
                ? `${formatNumber(summary.overCapacityCount)} over allocation by ${formatNumber(summary.totalSeatsOver)} seats`
                : "No over-allocation"}
            </Text>
          </Group>
          <Text
            titleStyle="h6"
            c={summary.overCapacityCount > 0 ? "red.7" : "green.7"}
          >
            {summary.overCapacityCount > 0 ? "Action Needed" : "Healthy"}
          </Text>
        </Group>
      </Card>
      {isLoading ? (
        <Text size="sm" c="slate.6">
          Loading...
        </Text>
      ) : items.length === 0 ? (
        <Text size="sm" c="slate.6">
          {emptyMessage}
        </Text>
      ) : (
        <Stack gap="xs">
          {items.map((item, index) => (
            <Stack key={item.id} gap="xs">
              {secondarySectionLabel &&
              secondarySectionStartIndex !== undefined &&
              index === secondarySectionStartIndex &&
              secondarySectionStartIndex < items.length ? (
                <Card
                  p="sm"
                  mt="xl"
                  bg="yellow.0"
                  style={{
                    border: "2px solid #fcc419",
                    borderLeft: "8px solid #f59f00",
                  }}
                >
                  <Text size="sm" fw={700} c="yellow.9" ta="center">
                    ▼ ▼ ▼ {secondarySectionLabel} ▼ ▼ ▼
                  </Text>
                </Card>
              ) : null}
              <Card
                p="md"
                bg="white"
                style={{
                  border: item.isOverCapacity
                    ? "1px solid #ffa8a8"
                    : "1px solid #e9ecef",
                  maxHeight: item.assignmentRows?.length ? 420 : undefined,
                  overflowY: item.assignmentRows?.length ? "auto" : undefined,
                }}
              >
                <Stack gap="sm">
                  <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <Text titleStyle="h4">{item.title}</Text>
                    <Group gap="xs" wrap="nowrap">
                      <Button
                        title="Edit"
                        variant="light"
                        color="primary"
                        size="xs"
                        onClick={item.onEdit}
                      >
                        Edit
                      </Button>
                      <Button
                        title="Delete"
                        variant="subtle"
                        color="red"
                        size="xs"
                        onClick={item.onDelete}
                      >
                        Delete
                      </Button>
                    </Group>
                  </Group>
                  <Group gap="xl" wrap="wrap" align="flex-end">
                    <Stack gap={0}>
                      <Text size="xs" c="slate.6" fw={600}>
                        Enrolled
                      </Text>
                      <Text titleStyle="h3" c="slate.8">
                        {formatNumber(item.enrolledSeats)}
                      </Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text size="xs" c="slate.6" fw={600}>
                        Started
                      </Text>
                      <Text
                        titleStyle="h3"
                        c={item.isOverCapacity ? "red.7" : "primary.8"}
                      >
                        {formatNumber(item.usedSeats)}
                      </Text>
                    </Stack>
                    <Stack gap={0}>
                      <Text size="xs" c="slate.6" fw={600}>
                        Seats
                      </Text>
                      <Text titleStyle="h3" c="slate.8">
                        {formatNumber(item.allocatedSeats)}
                      </Text>
                    </Stack>
                    <Text
                      size="sm"
                      fw={600}
                      c={item.isOverCapacity ? "red.7" : "green.7"}
                    >
                      {item.isOverCapacity
                        ? `Over allocation: ${formatNumber(item.overBy)}`
                        : `Remaining: ${formatNumber(item.seatsRemaining)}`}
                    </Text>
                  </Group>

                  {item.assignmentRows?.length ? (
                    <Stack gap={6}>
                      <Table.ScrollContainer minWidth={560}>
                        <Table
                          striped
                          highlightOnHover
                          withTableBorder
                          withColumnBorders
                        >
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Assignment</Table.Th>
                              <Table.Th>Owner Email</Table.Th>
                              <Table.Th style={{ width: 80 }}>
                                Enrolled
                              </Table.Th>
                              <Table.Th style={{ width: 80 }}>Started</Table.Th>
                              <Table.Th style={{ width: 190 }}>
                                Created At
                              </Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {item.assignmentRows.map((row) => (
                              <Table.Tr key={row.assignmentId}>
                                <Table.Td>
                                  <Text size="xs">{row.assignmentTitle}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs" c="slate.6">
                                    {row.ownerEmail}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs">{row.totalStudents}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text
                                    size="xs"
                                    c={
                                      row.startedStudents > 0
                                        ? "primary.8"
                                        : "slate.6"
                                    }
                                    fw={row.startedStudents > 0 ? 600 : 400}
                                  >
                                    {row.startedStudents}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs" c="slate.6">
                                    {row.createdAt
                                      ? new Date(row.createdAt).toLocaleString()
                                      : "Unknown"}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                    </Stack>
                  ) : null}
                </Stack>
              </Card>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function AssignmentInfo(props: {
  assignmentData: any;
  users: any;
  videoDataByUser: any;
  assignmentResponses: any;
}) {
  const { assignmentData, users, assignmentResponses } = props;
  const [activeTab, setActiveTab] = useState("responses");
  const [activePromptId, setActivePromptId] = useState<string | null>(null);

  useEffect(() => {
    if (assignmentData && !activePromptId) {
      setActivePromptId(Object.keys(assignmentData.prompts || {})[0]);
    }
  }, [assignmentData, activePromptId]);

  const assignmentOwnerId = useMemo(() => {
    return Object.entries(assignmentData?.users || {}).find(
      ([, user]: any) => user.role === "owner",
    )?.[0];
  }, [assignmentData]);

  const assignmentOwner = useMemo(() => {
    return assignmentOwnerId ? users[assignmentOwnerId] : null;
  }, [assignmentOwnerId, users]);

  const AdminAssignmentResponses = useMemo(() => {
    if (!assignmentData) return null;

    // Get responses for the current assignment
    const currentAssignmentResponses =
      assignmentResponses[assignmentData.id] || {};

    // Get responses for the active prompt
    const activePromptResponses = activePromptId
      ? Object.entries(currentAssignmentResponses)
          .map(([userId, userResponses]: any) => {
            const response =
              userResponses?.responses?.[activePromptId]?.response;
            const user = users[userId];
            return response
              ? {
                  ...userResponses?.responses?.[activePromptId],
                  userId,
                  user,
                  response,
                }
              : null;
          })
          .filter(Boolean)
      : [];

    return (
      <Stack gap="xs" pt="sm" w="100%">
        {Object.entries(assignmentData.prompts || {}).map(
          ([promptId, promptData]: any) => {
            const isActive = activePromptId === promptId;
            const promptResponseCount = Object.values(
              currentAssignmentResponses || {},
            ).filter(
              (userResponses: any) =>
                userResponses?.responses?.[promptId]?.response,
            ).length;

            return (
              <Button
                key={promptId}
                title={promptData.prompt}
                variant={isActive ? "filled" : "light"}
                color={isActive ? "primary" : "slate"}
                size="xs"
                onClick={() => setActivePromptId(promptId)}
                w={500}
              >
                <Text
                  size="xs"
                  c={isActive ? "white" : "slate.6"}
                  ml="xs"
                  lineClamp={1}
                  mr="xs"
                >
                  {promptData.prompt}
                </Text>
                ({promptResponseCount})
              </Button>
            );
          },
        )}
        <ScrollArea h={400} w="100%">
          <Stack gap="sm">
            {activePromptResponses.map((responseData: any, index: number) => {
              if (!responseData) return null;
              return (
                <Card
                  key={`${responseData.userId}-${index}`}
                  p="sm"
                  bg="white"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                  }}
                >
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start">
                      {responseData.timestamp && (
                        <Text size="xs" c="slate.5">
                          {new Date(responseData.timestamp).toLocaleString()}
                        </Text>
                      )}
                    </Group>
                    <Text size="sm" style={{ lineHeight: 1.5 }}>
                      {responseData.response}
                    </Text>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </ScrollArea>
      </Stack>
    );
  }, [activePromptId, assignmentData, assignmentResponses, users]);

  if (!assignmentData) return null;

  return (
    <Stack align="flex-start" justify="flex-start" flex={1} gap={0} mt="xl">
      <Stack gap="4px">
        <Text titleStyle="h3" style={{ textDecoration: "underline" }}>
          {assignmentData.title}
        </Text>
        <Text size="sm" c="slate.8">
          <strong>Assignment ID:</strong>{" "}
          <Link to={`/assignments/${assignmentData.id}`} target="_blank">
            {assignmentData.id}
          </Link>
        </Text>
        <Text size="sm" c="slate.8">
          <strong>Created by:</strong> {assignmentOwner?.email}
        </Text>
        <Text size="sm" c="slate.8">
          <strong>Created at:</strong>{" "}
          {new Date(assignmentData.createdAt).toLocaleString()}
        </Text>
        <Text size="sm" c="slate.8">
          <strong>Total Students:</strong> {assignmentData.totalStudents}
        </Text>
        <Text size="sm" c="slate.8">
          <strong>Assignment Type:</strong> {assignmentData.assignmentType}
        </Text>
        <Text size="sm" c="slate.8">
          <strong>Required Count:</strong> {assignmentData.requiredCount}
        </Text>
      </Stack>

      <Space h="md" />
      <Text titleStyle="h5">Instructions</Text>
      <Text size="sm" c="slate.6">
        {assignmentData.instructions}
      </Text>

      <Space h="md" />

      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value || "responses")}
        w="100%"
      >
        <Tabs.List>
          <Tabs.Tab value="responses">Responses</Tabs.Tab>
          <Tabs.Tab value="videoData">Video Data</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="responses">{AdminAssignmentResponses}</Tabs.Panel>
        <Tabs.Panel value="videoData">
          <Text>Video Data</Text>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
